import { Flex, Spinner, VStack } from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import OrdersList from '@components/OrdersList';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserRemittanceOrdersReqData, updateUserBBPSOrdersReqData } from '@store/actions/reqDataActions';
import useDidMountEffect from '@hooks/useDidMount';
import {
  clearUserOrders,
  clearUserGomsOrders,
  getUserGomsOrders,
  getUserOrders,
  gomsUserSearchOrders,
  clearUserBBPSOrders
} from '@store/actions/usersActions';
import useIsMobile from '@hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import { ORDER_RESOURCE, ORDER_PRODUCTS } from '@constants';

const UserTransfers = ({
  userId,
  setSelectedOrder,
  maxW = 'none',
  isSidebar = false,
  orderResource = ORDER_RESOURCE.APP_SERVER,
  orderType = ORDER_PRODUCTS.REMITTANCE
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Get orders based on the current orderResource and orderType
  const orders = useSelector((state) => {
    if (orderType === ORDER_PRODUCTS.BBPS) {
      return state.users.userDetails.bbpsOrders;
    }
    return orderResource === ORDER_RESOURCE.APP_SERVER
      ? state.users.userDetails.orders
      : state.users.userDetails.gomsOrders;
  });

  const { data, totalPages, isLoading } = orders;

  const searchReqData = useSelector((state) => {
    if (orderType === ORDER_PRODUCTS.BBPS) {
      return state.reqData.user.orders.bbps || { pageNo: 0, pageSize: 10 };
    }
    return state.reqData.user.orders.remittance;
  });

  // Initial data loading - use regular useEffect to ensure it runs on mount
  useEffect(() => {
    // For BBPS orders, use the new gomsUserSearchOrders API
    if (orderType === ORDER_PRODUCTS.BBPS) {
      dispatch(
        gomsUserSearchOrders({
          query: '',
          orderType: ORDER_PRODUCTS.BBPS,
          userId,
          page: searchReqData.pageNo || 0,
          pageSize: searchReqData.pageSize || 10
        })
      );
    } else {
      if (orderResource === ORDER_RESOURCE.APP_SERVER) {
        dispatch(getUserOrders({ userId, ...searchReqData }));
      } else if (orderResource === ORDER_RESOURCE.GOMS_SERVICE) {
        dispatch(getUserGomsOrders({ userId, ...searchReqData }));
      }
    }
  }, [userId, orderResource, orderType, searchReqData, dispatch]);

  // Cleanup on unmount - use useDidMountEffect to avoid unnecessary cleanup on prop changes
  useDidMountEffect(() => {
    return () => {
      // Clear orders when component unmounts
      if (orderType === ORDER_PRODUCTS.BBPS) {
        dispatch(clearUserBBPSOrders());
      } else if (orderResource === ORDER_RESOURCE.APP_SERVER) {
        dispatch(clearUserOrders());
      } else if (orderResource === ORDER_RESOURCE.GOMS_SERVICE) {
        dispatch(clearUserGomsOrders());
      }
    };
  }, [orderResource, orderType, dispatch]);

  const handlePageChange = useCallback(
    (pageNo) => {
      pageNo - 1 !== searchReqData.pageNo &&
        (orderType === ORDER_PRODUCTS.BBPS
          ? dispatch(updateUserBBPSOrdersReqData({ pageNo: pageNo - 1 }))
          : dispatch(updateUserRemittanceOrdersReqData({ pageNo: pageNo - 1 }))
        );
    },
    [dispatch, searchReqData.pageNo, orderType]
  );

  const onOrderDetailsClick = useCallback(
    (orderId, userId, orderResource) => {
      if (isMobile) {
        // If on mobile, navigate to order details page
        navigate(
          `/order/${orderId}?orderResource=${
            orderResource || ORDER_RESOURCE.APP_SERVER
          }`
        );
      } else {
        // If on desktop, just set the selected order (sidebar will show details)
        setSelectedOrder({
          product: orderType, // Use the orderType prop instead of hardcoded PRODUCT.REMITTANCE
          orderId,
          orderResource: orderResource || ORDER_RESOURCE.APP_SERVER
        });
      }
    },
    [isMobile, navigate, setSelectedOrder, orderResource, orderType]
  );

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" w="full" h="full">
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
      </Flex>
    );
  }

  return (
    <VStack
      w={isSidebar ? '95%' : '100%'}
      alignItems={'stretch'}
      gap={'0.5rem'}
      maxW={maxW}
    >
      <OrdersList
        orders={data}
        pageNo={searchReqData.pageNo}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        onOrderDetailsClick={onOrderDetailsClick}
        show={'sourceAccount'}
        orderResource={orderResource}
        isSidebar={isSidebar}
      />
    </VStack>
  );
};

export default UserTransfers;
